import React, { useEffect, useMemo, useRef, useCallback } from 'react';
import { Card, Tabs, Tab, Block, Space, Icon, Text } from '@mc/ui-v4';
import { mcInfoFilled } from '@mc/react-ui-icons';
import { ClientMode, MapEditor, EditorLayersMode } from '@mc/react-mapio';
import { getZboZakresyEtap, getAgZakresyEtap, parseFeaturesFromZakresy, getEtapyCount, createFeaturePointers } from '../helpers/mapioHelpers';
import { EtapyOverview } from './EtapyOverview';
import { EtapaDetail } from './EtapaDetail';
import { useAppConfig } from '@mc/react-app-base';
const MAPIO_TOPIC = 'ag_sh:zbo';
const ZBO_ZAKRES_MAPIO_LAYER = 'ag_sh:karta_zakres';
const AG_ZAKRES_MAPIO_LAYER = 'ag_sh:karta_zakres_ag';
export const MapWithEtapy = ({ activeInfo, activeTab, onTabChange, auth }) => {
    var _a, _b;
    const appConfig = useAppConfig();
    console.log('<<< appConfig >>>', appConfig);
    const mapioRef = useRef(null);
    const [isMapioReady, setIsMapioReady] = React.useState(false);
    const zboZakresyEtap = useMemo(() => {
        return getZboZakresyEtap(activeInfo);
    }, [activeInfo]);
    const agZakresyEtap = useMemo(() => {
        return getAgZakresyEtap(activeInfo);
    }, [activeInfo]);
    useEffect(() => {
        if (!(activeInfo === null || activeInfo === void 0 ? void 0 : activeInfo.pripad) || !isMapioReady || !mapioRef.current) {
            return;
        }
        mapioRef.current.unloadFeatures();
        loadMapioFeatures();
    }, [activeInfo, isMapioReady, activeTab, zboZakresyEtap, agZakresyEtap]);
    const loadMapioFeatures = useCallback(() => {
        if (!mapioRef.current || !(activeInfo === null || activeInfo === void 0 ? void 0 : activeInfo.pripad))
            return;
        if (activeTab === -1) {
            loadAllEtapyFeatures();
        }
        else {
            loadSingleEtapaFeatures();
        }
    }, [activeTab, activeInfo, zboZakresyEtap, agZakresyEtap]);
    /**
     * Načte zákresy do vrstev mapia bez zoomu a nakonec provede jediný zoom (a vycentrování)
     * nad všemi načtenými zákresy dohromady. Jednotlivé loadFeatures se zoomem by totiž
     * zoomovaly pouze na svou vrstvu, takže v záložce "Vše" nebyly vidět všechny etapy (MCW-28695).
     */
    const loadFeaturesAndZoom = useCallback((featuresByLayer) => {
        if (!mapioRef.current)
            return;
        const featurePointers = [];
        featuresByLayer.forEach(({ layerId, features }) => {
            mapioRef.current.loadFeatures(features, { zoom: false }, layerId);
            featurePointers.push(...createFeaturePointers(features, layerId));
        });
        if (featurePointers.length > 0) {
            // zoomMinScale drží stejné omezení přiblížení jako klient ZBO (u malých zákresů)
            mapioRef.current.zoomOnFeatures(featurePointers, { zoomMinScale: 500 });
        }
    }, []);
    const loadAllEtapyFeatures = useCallback(() => {
        if (!mapioRef.current)
            return;
        const agParsedFeatures = parseFeaturesFromZakresy(agZakresyEtap);
        const etapyCount = getEtapyCount(activeInfo);
        const featuresByLayer = [];
        for (let index = 0; index < etapyCount; index++) {
            const zboParsedFeature = zboZakresyEtap[index] ? JSON.parse(zboZakresyEtap[index]) : null;
            console.log('<<<< LOADING_ETAPA', `${ZBO_ZAKRES_MAPIO_LAYER}_etapa${index + 1}`);
            featuresByLayer.push({
                layerId: `${ZBO_ZAKRES_MAPIO_LAYER}_etapa${index + 1}`,
                features: [zboParsedFeature].flat().filter(Boolean),
            });
        }
        featuresByLayer.push({ layerId: AG_ZAKRES_MAPIO_LAYER, features: agParsedFeatures.flat() });
        loadFeaturesAndZoom(featuresByLayer);
    }, [zboZakresyEtap, agZakresyEtap, activeInfo, loadFeaturesAndZoom]);
    const loadSingleEtapaFeatures = useCallback(() => {
        if (!mapioRef.current || activeTab === -1)
            return;
        const zboParsedFeature = zboZakresyEtap[activeTab] ? JSON.parse(zboZakresyEtap[activeTab]) : null;
        const agParsedFeature = agZakresyEtap[activeTab] ? JSON.parse(agZakresyEtap[activeTab]) : null;
        loadFeaturesAndZoom([
            { layerId: `${ZBO_ZAKRES_MAPIO_LAYER}_etapa${activeTab + 1}`, features: [zboParsedFeature].flat().filter(Boolean) },
            { layerId: AG_ZAKRES_MAPIO_LAYER, features: [agParsedFeature].flat().filter(Boolean) },
        ]);
    }, [zboZakresyEtap, agZakresyEtap, activeTab, loadFeaturesAndZoom]);
    if (!(activeInfo === null || activeInfo === void 0 ? void 0 : activeInfo.pripad)) {
        return (React.createElement("div", { className: 'h-100 d-flex flex-column justify-content-center align-items-center p-3 text-center' },
            React.createElement(Icon, { icon: mcInfoFilled, intent: 'secondary', size: '4x', className: 'mb-2' }),
            React.createElement(Text, { style: { maxWidth: 256, fontSize: 15 } }, "P\u0159\u00EDpad je v ZBO evidov\u00E1n. Jeho zpracovatel dosud neposlal \u017E\u00E1dn\u00E9 up\u0159es\u0148uj\u00EDc\u00ED \u00FAdaje.")));
    }
    const etapyCount = getEtapyCount(activeInfo);
    const showEtapyOverview = etapyCount > 0 && activeTab === -1;
    const showEtapaDetail = etapyCount > 0 && activeTab !== -1;
    return (React.createElement(Card, { mode: 'edit', bodyClassName: 'd-flex flex-column h-100', style: { flex: 1, border: '1px solid #d8e4f5' } },
        React.createElement(Block, { title: React.createElement(Tabs, null,
                React.createElement(Tab, { active: activeTab === -1, onSelect: () => onTabChange(-1) }, "V\u0161e"),
                Array.from({ length: etapyCount }).map((_, index) => {
                    var _a, _b;
                    const caseId = (_b = (_a = activeInfo === null || activeInfo === void 0 ? void 0 : activeInfo.pripad) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : 'unknown';
                    return (React.createElement(Tab, { key: `etapa-${caseId}-${index}`, active: activeTab === index, onSelect: () => onTabChange(index) },
                        "Etapa ",
                        index + 1));
                })) },
            React.createElement(Space, { vertical: true },
                React.createElement(Card, { className: "pb-0 mb-0" },
                    React.createElement(MapEditor, { className: 'h-100 w-100 border-0 map-editor-detail', style: { minHeight: 420, marginBottom: -5 }, clientMode: ClientMode.NEW_EDITOR, layersMode: EditorLayersMode.OVERLAY, 
                        //@ts-expect-error - jsou to specifické typy dané aplikace
                        serverUrl: ((_a = appConfig.dependUrls) === null || _a === void 0 ? void 0 : _a.mapio) || globalThis.MAPIO_URL || (globalThis.location.origin + '/mapio'), tools: [], externalLayersEnabled: false, layerSettingsEnabled: false, authentication: (_b = auth === null || auth === void 0 ? void 0 : auth.data) === null || _b === void 0 ? void 0 : _b.login, initialTopic: MAPIO_TOPIC, ref: mapioRef, onReady: () => setIsMapioReady(true) })),
                React.createElement(Card, { mode: 'view', style: { height: 180, overflowY: 'auto' } },
                    React.createElement(Block, null,
                        showEtapyOverview && React.createElement(EtapyOverview, { activeInfo: activeInfo }),
                        showEtapaDetail && React.createElement(EtapaDetail, { activeInfo: activeInfo, etapaIndex: activeTab })))))));
};
//# sourceMappingURL=MapWithEtapy.js.map